import { Controller } from "@hotwired/stimulus"

const PENDING_SCROLL_STORAGE_KEY = "evercooked:pagination-scroll"
const MAX_TARGET_LOOKUP_ATTEMPTS = 3
const BROWSE_FILTER_HISTORY_RESTORE_EVENT = "browse-filter-history-restore"

export default class extends Controller {
  connect() {
    this.handleClick = this.handleClick.bind(this)
    this.handleSubmitStart = this.handleSubmitStart.bind(this)
    this.handleTurboVisit = this.handleTurboVisit.bind(this)
    this.handleTurboLoad = this.handleTurboLoad.bind(this)
    this.handleFrameRender = this.handleFrameRender.bind(this)
    this.handlePageShow = this.handlePageShow.bind(this)
    this.handleBrowseFilterHistoryRestore = this.handleBrowseFilterHistoryRestore.bind(this)

    document.addEventListener("click", this.handleClick, true)
    document.addEventListener("turbo:submit-start", this.handleSubmitStart, true)
    document.addEventListener("turbo:visit", this.handleTurboVisit)
    document.addEventListener("turbo:load", this.handleTurboLoad)
    document.addEventListener("turbo:frame-render", this.handleFrameRender)
    document.addEventListener(BROWSE_FILTER_HISTORY_RESTORE_EVENT, this.handleBrowseFilterHistoryRestore)
    window.addEventListener("pageshow", this.handlePageShow)
  }

  disconnect() {
    document.removeEventListener("click", this.handleClick, true)
    document.removeEventListener("turbo:submit-start", this.handleSubmitStart, true)
    document.removeEventListener("turbo:visit", this.handleTurboVisit)
    document.removeEventListener("turbo:load", this.handleTurboLoad)
    document.removeEventListener("turbo:frame-render", this.handleFrameRender)
    document.removeEventListener(BROWSE_FILTER_HISTORY_RESTORE_EVENT, this.handleBrowseFilterHistoryRestore)
    window.removeEventListener("pageshow", this.handlePageShow)
  }

  handleClick(event) {
    if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return

    const link = event.target.closest?.("[data-pagination-scroll-link]")
    const href = link?.getAttribute("href")
    if (!link || !href) return

    const region = link.dataset.paginationScrollLink
    if (!region) return

    const destination = new URL(href, window.location.href)
    const frame = link.closest("turbo-frame")
    const handledByBrowseFilterForm = link.dataset.action?.includes("browse-filter-form#paginate")
    const frameId = frame && (link.dataset.turbo !== "false" || handledByBrowseFilterForm) ? frame.id : null

    this.rememberPendingScroll({
      destination: `${destination.pathname}${destination.search}`,
      frameId,
      id: `${Date.now()}-${Math.random()}`,
      region
    })
  }

  handleSubmitStart(event) {
    const form = event.target
    if (form?.tagName !== "FORM") return

    // browse-filter-form marks pagination submissions explicitly. Any other
    // submission from the same browse surface supersedes pending pagination,
    // even though both transitions use Turbo's "advance" history action.
    if (form.dataset.browseFilterSubmission !== "pagination") {
      this.clearPendingScroll()
    }
  }

  handleTurboVisit(event) {
    const action = event.detail?.action
    if (action === "advance" || action === "restore") {
      this.resetTurboReloadState()
    }

    if (action === "restore") {
      // Turbo owns restoration visits and their saved scroll positions.
      this.clearPendingScroll()
    }
  }

  handleBrowseFilterHistoryRestore() {
    // browse-filter-results is the sole popstate/history owner. This event
    // lets pagination compose with its restoration path without another
    // popstate listener or History API state.
    this.clearPendingScroll()
  }

  handlePageShow(event) {
    if (event.persisted || this.navigationType() === "back_forward") {
      // A non-Turbo page load lets the browser restore its own scroll state.
      this.clearPendingScroll()
    }
  }

  handleTurboLoad() {
    const pendingScroll = this.readPendingScroll()
    if (!pendingScroll) return

    if (!this.locationMatches(pendingScroll.destination)) {
      this.clearPendingScroll(pendingScroll)
      return
    }

    this.scrollToPendingRegion(pendingScroll)
  }

  resetTurboReloadState() {
    const view = window.Turbo?.navigator?.view
    if (view?.forceReloaded) {
      // A promoted frame visit can mark Turbo's page view as reloaded even
      // though it did not replace the document. Clear that stale state before
      // Turbo performs the next advance or restoration scroll.
      // `forceReloaded` is Turbo-internal; review this workaround when
      // upgrading @hotwired/turbo-rails.
      view.forceReloaded = false
    }
  }

  handleFrameRender(event) {
    const pendingScroll = this.readPendingScroll()
    if (!pendingScroll || !pendingScroll.frameId || event.target?.id !== pendingScroll.frameId) return

    this.scrollToPendingRegion(pendingScroll)
  }

  rememberPendingScroll(pendingScroll) {
    try {
      window.sessionStorage.setItem(PENDING_SCROLL_STORAGE_KEY, JSON.stringify(pendingScroll))
    } catch (_error) {
      // A storage-restricted browser can still use the pagination normally.
    }
  }

  readPendingScroll() {
    try {
      const value = window.sessionStorage.getItem(PENDING_SCROLL_STORAGE_KEY)
      return value ? JSON.parse(value) : null
    } catch (_error) {
      return null
    }
  }

  clearPendingScroll(pendingScroll = this.readPendingScroll()) {
    if (!pendingScroll) return

    try {
      if (this.readPendingScroll()?.id === pendingScroll.id) {
        window.sessionStorage.removeItem(PENDING_SCROLL_STORAGE_KEY)
      }
    } catch (_error) {
      // Nothing else is required when session storage is unavailable.
    }
  }

  navigationType() {
    try {
      return window.performance.getEntriesByType("navigation")[0]?.type
    } catch (_error) {
      return null
    }
  }

  locationMatches(destination) {
    const current = new URL(window.location.href)
    const target = new URL(destination, window.location.href)

    return current.pathname === target.pathname && current.search === target.search
  }

  scrollToPendingRegion(pendingScroll, attempt = 0) {
    window.requestAnimationFrame(() => {
      const target = this.findTarget(pendingScroll.region, pendingScroll.frameId)

      if (target) {
        this.clearPendingScroll(pendingScroll)
        target.scrollIntoView({ behavior: "auto", block: "start", inline: "nearest" })
        return
      }

      if (attempt < MAX_TARGET_LOOKUP_ATTEMPTS) {
        this.scrollToPendingRegion(pendingScroll, attempt + 1)
      } else {
        this.clearPendingScroll(pendingScroll)
      }
    })
  }

  findTarget(region, frameId) {
    const scope = frameId ? document.getElementById(frameId) : document
    if (!scope) return null

    if (scope.dataset?.paginationScrollTarget === region) return scope

    return Array.from(scope.querySelectorAll("[data-pagination-scroll-target]")).find(
      (element) => element.dataset.paginationScrollTarget === region
    )
  }
}
